// Ad-hoc treasury wallet ACTIVITY feed (manager-only, N8-internal).
// GET /api/v1/ops/wallet-activity — unified recent money movement across the
// on-chain treasury wallets (Moralis/Arkham/Sui-backed), server-cached 15 min.
// Companion to WalletBalances; NOT the Account/PositionSnapshot pipeline —
// see api/apps/portfolio/services/wallet_activity.py.

import { useState } from 'react';

import { PageHeader, Pill, SortTable, Stat, TreasuryTabs, SourcesUnavailableBanner } from '../../components/shell';
import { fmtBase } from '../../lib/format';
import { api } from '../../lib/api';
import { usePolling } from '../../lib/usePolling';

const POLL_MS = 60_000;

interface ActivityRow {
  timestamp: string | null;
  wallet_label: string;
  chain: string;
  symbol: string;
  direction: string; // in | out | trade
  amount: string | null;
  usd_value: number | null;
  tx_hash: string | null;
  explorer_url: string | null;
}

interface SkippedEntry {
  wallet_label?: string;
  reason: string;
  // item 29 — every entry declares which it is. Optional so an older cached
  // payload still renders; anything without a kind falls through to notes,
  // which is the quiet side. An outage must be opted IN to the loud side,
  // never defaulted into it.
  kind?: 'error' | 'gap' | 'note';
}

interface WalletActivityPayload {
  as_of: string;
  rows: ActivityRow[];
  skipped: SkippedEntry[];
  cache_hit: boolean;
  include_trades: boolean;
}

export default function WalletActivity() {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [includeTrades, setIncludeTrades] = useState(false);

  const query = includeTrades ? '?trades=1' : '';
  const { data, error, loading, refresh } = usePolling<WalletActivityPayload>(
    (signal) => api<WalletActivityPayload>(`/ops/wallet-activity${query}`, { signal }),
    { intervalMs: POLL_MS, deps: [refreshNonce, includeTrades] },
  );

  const handleForceRefresh = () => {
    setRefreshNonce(n => n + 1);
    const refreshQuery = includeTrades ? '?refresh=1&trades=1' : '?refresh=1';
    api(`/ops/wallet-activity${refreshQuery}`).then(() => refresh()).catch(() => refresh());
  };

  if (error) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>
          Failed to load treasury activity.
        </p>
      </div>
    );
  }
  if (!data && loading) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }
  if (!data) return null;

  const asOfText = new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

  const sortableLabel = (text: string) => (
    <>
      {text} <span style={{ opacity: 0.4, fontSize: 10 }}>⇅</span>
    </>
  );

  const shortHash = (h: string) => (h.length > 14 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h);

  // Direction Pill tone mirrors TypePill's flow convention: inflow≈deposit
  // (accent), outflow≈withdrawal (warn), swap/trade neutral.
  const flowTone = (d: string) => (d === 'in' ? 'accent' : d === 'out' ? 'warn' : '');

  // Sort accessors: `amount` arrives as a string and `timestamp` may be null,
  // so give SortTable a numeric key (else it falls back to lexicographic
  // string compare, which mis-orders '1000000' vs '2').
  const tsSort = (r: ActivityRow) => {
    if (!r.timestamp) return 0;               // null/unparseable → sorts last on desc
    const t = new Date(r.timestamp).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const amtSort = (r: ActivityRow) => {
    if (r.amount == null) return null;        // SortTable pushes null to the bottom
    const n = Number(r.amount);
    return Number.isFinite(n) ? n : null;
  };

  const columns = [
    {
      key: 'timestamp', label: sortableLabel('WHEN (UTC)'), sortBy: tsSort,
      render: (r: ActivityRow) => (
        <span className="num">{r.timestamp ? new Date(r.timestamp).toISOString().slice(0, 16).replace('T', ' ') : '—'}</span>
      ),
    },
    { key: 'wallet_label', label: sortableLabel('ACCOUNT') },
    { key: 'chain', label: sortableLabel('CHAIN') },
    { key: 'symbol', label: sortableLabel('ASSET') },
    {
      key: 'direction', label: sortableLabel('FLOW'),
      render: (r: ActivityRow) => <Pill tone={flowTone(r.direction)}>{(r.direction || '').toUpperCase()}</Pill>,
    },
    {
      key: 'amount', label: sortableLabel('AMOUNT'), align: 'right' as const, sortBy: amtSort,
      render: (r: ActivityRow) => {
        if (r.amount == null) return <span className="num">—</span>;
        const n = Number(r.amount);
        return <span className="num">{Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : r.amount}</span>;
      },
    },
    {
      key: 'usd_value', label: sortableLabel('VALUE (USD)'), align: 'right' as const,
      render: (r: ActivityRow) => <span className="num">{r.usd_value == null ? '—' : fmtBase(r.usd_value, 'USD')}</span>,
    },
    {
      key: 'tx_hash', label: 'TX',
      render: (r: ActivityRow) => {
        if (!r.tx_hash) return <span style={{ color: 'var(--ink-3)' }}>—</span>;
        const short = shortHash(r.tx_hash);
        return r.explorer_url ? (
          <a href={r.explorer_url} target="_blank" rel="noopener noreferrer" className="btn-link" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
            {short}
          </a>
        ) : (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{short}</span>
        );
      },
    },
  ];

  // Split once, here, so both lists below read from the same rule.
  const errorSkips = data.skipped.filter(s => s.kind === 'error');
  const noteSkips = data.skipped.filter(s => s.kind !== 'error');

  return (
    <div className="view container">
      {/* Above the notice AND the header: it qualifies every figure below. */}
      <SourcesUnavailableBanner down={errorSkips.length} />

      <div style={{
        marginBottom: 20, padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--tint)', border: `1px solid var(--accent)`,
        color: 'var(--accent)',
        fontFamily: 'var(--sans)', fontSize: 13,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 3 L22 20 L2 20 Z" stroke="var(--accent)" strokeWidth="1.75" strokeLinejoin="round" />
          <line x1="12" y1="9" x2="12" y2="14" stroke="var(--accent)" strokeWidth="1.75" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="var(--accent)" />
        </svg>
        This page is still being developed; sources are still being added.
      </div>

      <TreasuryTabs active="activity" />

      <PageHeader
        kicker="OPS"
        title="Treasury wallet activity"
        meta={(
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ display: 'flex', border: 'var(--hairline)' }}>
              {([['MOVEMENT', false], ['INCLUDE TRADES', true]] as const).map(([label, val]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setIncludeTrades(val)}
                  className="label-s"
                  style={{
                    cursor: 'pointer', padding: '4px 12px', border: 'none',
                    background: includeTrades === val ? 'var(--ink)' : 'transparent',
                    color: includeTrades === val ? 'var(--paper)' : 'var(--ink)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="button" onClick={handleForceRefresh} className="label-s" style={{ cursor: 'pointer' }}>
              REFRESH NOW
            </button>
          </div>
        )}
      />

      <div style={{ display: 'flex', gap: 48, marginBottom: 32 }}>
        <Stat
          label="TRANSACTIONS"
          value={data.rows.length.toLocaleString()}
          sub={`as of ${asOfText}${data.cache_hit ? ' · cached' : ' · live'}${data.include_trades ? ' · incl. trades' : ' · money movement'}`}
          large
        />
      </div>

      <SortTable
        columns={columns}
        rows={data.rows}
        defaultSort="timestamp"
        defaultDir="desc"
        emptyMessage="No recent activity found."
      />

      {/* item 29 — an OUTAGE and an informational note used to render as the
          same muted bullet, so the channel's only signal was "non-empty means
          look at me". Item 28 made it permanently non-empty for the live Sui
          wallet, which spent that signal. Failures are now separated and
          oxblood; everything else stays quiet. */}
      {errorSkips.length > 0 && (
        <div className="label-s" style={{ marginTop: 32, color: 'var(--oxblood)' }}>
          <h2 className="label-s" style={{ marginBottom: 8 }}>SOURCES UNAVAILABLE — TREASURY FIGURES ARE INCOMPLETE:</h2>
          <ul style={{ margin: 0, paddingLeft: 20, fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 14, letterSpacing: 'normal', textTransform: 'none' }}>
            {errorSkips.map((s, i) => (
              <li key={i}>{s.wallet_label ? `${s.wallet_label} — ${s.reason}` : s.reason}</li>
            ))}
          </ul>
        </div>
      )}
      {noteSkips.length > 0 && (
        <div className="label-s" style={{ marginTop: 32, color: 'var(--ink-3)' }}>
          <h2 className="label-s" style={{ marginBottom: 8 }}>DATA NOTES:</h2>
          <ul style={{ margin: 0, paddingLeft: 20, fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 14, letterSpacing: 'normal', textTransform: 'none' }}>
            {noteSkips.map((s, i) => (
              <li key={i}>{s.wallet_label ? `${s.wallet_label} — ${s.reason}` : s.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
